import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Gauge, Loader2, Search, ShieldCheck, Sparkles } from "lucide-react";
import { API } from "../config";
import { trackEvent } from "../hooks/useBehaviorTracker";
import { PersonaHeader } from "../components/persona/PersonaSections";
import { HeroBuyButton } from "../components/common/ServiceOrderBundle";
import { ALLIN_DEADLINE } from "../data/funnelContent";
import { CONGRATS_UI, findCongrats } from "../data/congratsFunnel";
import { EmailResult, ProgressBox } from "../components/congrats/CongratsResultExtras";

const CSS = `
@keyframes gUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
@keyframes gTwinkle { 0%,100% { opacity: .15; transform: scale(.8); } 50% { opacity: .9; transform: scale(1.15); } }
.g-up { animation: gUp .7s cubic-bezier(.2,.7,.2,1) both; }
.g-spark { position: absolute; width: 4px; height: 4px; border-radius: 9999px; background: #fcd34d; box-shadow: 0 0 12px 3px rgba(252,211,77,.45); animation: gTwinkle 3.2s ease-in-out infinite; }
`;
const SPARKS = [[8, 12, 0], [22, 70, .6], [38, 18, 1.2], [55, 85, .3], [70, 10, 1.8], [84, 55, .9], [92, 22, 2.2], [15, 45, 1.5], [63, 40, 2.6], [46, 62, .2]];
const daysLeft = () => Math.max(0, Math.ceil((ALLIN_DEADLINE - new Date()) / 86400000));
const tone = (s) => (s == null ? "text-white/40" : s >= 80 ? "text-emerald-400" : s >= 60 ? "text-amber-300" : "text-red-400");

const ScoreTile = ({ icon: Icon, label, value, highlight, testid }) => (
  <div className={`rounded-xl border p-4 ${highlight ? "border-[#3b82f6] bg-[#3b82f6]/10" : "border-white/10 bg-white/[0.03]"}`} data-testid={testid}>
    <Icon className="w-5 h-5 text-white/60 mb-2" />
    <p className="text-[11px] uppercase tracking-wider text-white/50">{label}</p>
    <p className={`text-3xl font-black font-['JetBrains_Mono'] ${tone(value)}`}>{value ?? "—"}</p>
  </div>
);

const issueText = (i, ui, c) => ui[i.label] || (c.lang === "cz" ? `${ui.secIssue}: ${i.label}` : i.detail ? `${i.label}: ${i.detail}` : i.label);

const QuickResult = ({ res, ui, c, onReset }) => (
  <div className="g-up mt-8 rounded-2xl border border-white/10 bg-[#0d1530]/80 backdrop-blur-xl p-6 sm:p-8" data-testid="congrats-result">
    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
      <p className="text-white/70">{ui.resultTitle} <b className="text-white">{res.host}</b></p>
      <p className="text-sm text-white/60">{ui.overall}: <b className={`text-2xl font-black ${tone(res.overall)}`} data-testid="congrats-overall">{res.overall}/100</b></p>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <ScoreTile icon={Sparkles} label={ui.ai} value={res.scores.ai} highlight={c.focus === "ai"} testid="congrats-score-ai" />
      <ScoreTile icon={Gauge} label={`${ui.speed} · ${res.load_ms} ${ui.ms}`} value={res.scores.speed} highlight={c.focus === "speed"} testid="congrats-score-speed" />
      <ScoreTile icon={ShieldCheck} label={ui.security} value={res.scores.security} highlight={c.focus === "security"} testid="congrats-score-security" />
    </div>
    {res.progress && <ProgressBox pr={res.progress} ui={ui} issueText={(i) => issueText(i, ui, c)} />}
    <p className="mt-6 mb-2 text-sm font-bold text-white">{res.progress ? ui.nextTitle : ui.issuesTitle}</p>
    {res.issues.length ? (
      <ul className="space-y-1.5 text-sm text-white/80" data-testid="congrats-issues">
        {res.issues.map((i) => <li key={i.area + i.label} className="flex gap-2"><span className="text-red-400">•</span>{issueText(i, ui, c)}</li>)}
      </ul>
    ) : <p className="text-sm text-emerald-300" data-testid="congrats-all-good">{ui.allGood}</p>}
    <EmailResult checkId={res.check_id} ui={ui} />
    <div className="mt-6 flex flex-wrap items-center gap-4">
      <a href="#ponuka" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#2563eb] text-white font-bold hover:bg-[#1d4ed8] transition-colors" data-testid="congrats-to-offer">{ui.offerTitle} <ArrowRight className="w-4 h-4" /></a>
      <button onClick={onReset} className="text-sm text-white/60 underline hover:text-white" data-testid="congrats-reset">{ui.again}</button>
    </div>
    <p className="mt-4 text-[11px] text-white/40">{ui.note}</p>
  </div>
);

const CheckForm = ({ c, ui, onResult }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true); setErr("");
    trackEvent("quick_check_start", { funnel: c.slug });
    try {
      const r = await axios.post(`${API}/funnel-tools/quick-check`, { url: url.trim(), language: c.lang, funnel: c.slug });
      trackEvent("quick_check_done", { funnel: c.slug, overall: r.data.overall });
      onResult(r.data);
    } catch (ex) { setErr(ex?.response?.status === 429 ? ui.errRate : ui.err); }
    setBusy(false);
  };
  return (
    <div className="mt-8 max-w-xl">
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="congrats-form">
      <div className="flex-1 flex items-center gap-2 rounded-lg bg-white px-4 border-2 border-transparent focus-within:border-[#3b82f6]">
        <Search className="w-4 h-4 text-gray-400" />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={ui.placeholder} disabled={busy}
          className="w-full py-3.5 bg-white text-gray-900 placeholder-gray-400 outline-none" data-testid="congrats-url-input" />
      </div>
      <button type="submit" disabled={busy} className="px-6 py-3.5 rounded-lg bg-[#2563eb] text-white font-bold hover:bg-[#1d4ed8] active:scale-[.98] transition-[background-color,transform] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="congrats-submit">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />{ui.checking}</> : c.cta}
      </button>
    </form>
    {err && <p className="mt-3 text-sm text-red-300" data-testid="congrats-error">{err}</p>}
    </div>
  );
};

const CongratsOffer = ({ ui, lang }) => {
  const days = daysLeft();
  const plans = [
    { id: "allin_2026_monthly", name: ui.monthly, price: 79, unit: ui.unitMonth, note: ui.monthlyNote, rec: true },
    { id: "allin_2026_prepaid", name: ui.prepaid, price: 590, unit: ui.unitOnce, note: ui.prepaidNote },
  ];
  return (
    <section id="ponuka" className="relative max-w-5xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20" data-testid="congrats-offer">
      <span id="balicky" className="absolute -top-20" aria-hidden />
      <p className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold" data-testid="congrats-countdown">
        <Clock className="w-3.5 h-3.5" /> {ui.offerBadge} · {ui.daysLeft}: {days}
      </p>
      <h2 className="text-3xl sm:text-4xl font-black text-white font-['Cabinet_Grotesk'] mb-3">{ui.offerTitle}</h2>
      <p className="text-white/60 mb-8">{ui.offerSub}</p>
      <div className="grid md:grid-cols-2 gap-5">
        {plans.map((p) => (
          <div key={p.id} className={`rounded-2xl border p-6 transition-transform duration-300 hover:-translate-y-1 ${p.rec ? "border-[#3b82f6] bg-[#3b82f6]/10" : "border-white/10 bg-white/[0.03]"}`} data-testid={`congrats-plan-${p.id}`}>
            <h3 className="text-xl font-black text-white">{p.name}</h3>
            <p className="mt-2 text-4xl font-black text-white font-['JetBrains_Mono']">€{p.price}<span className="text-base text-white/50">{p.unit}</span></p>
            <p className="mt-2 mb-5 text-sm text-white/70 flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{p.note}</p>
            {days === 0 ? <p className="text-sm text-red-300">{ui.ended}</p> : <HeroBuyButton packageId={p.id} priceEur={p.price} label={`${ui.buy} €${p.price}`} testid={`congrats-buy-${p.id}`} lang={lang} />}
          </div>
        ))}
      </div>
      <a href={lang === "cs" ? "/cz/zebricek-zlepseni" : "/rebricek-zlepseni"} className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 underline hover:text-white" data-testid="congrats-leaderboard-link">
        {lang === "cs" ? "Podívejte se na žebříček zlepšení webů" : "Pozrite si rebríček zlepšení webov"} <ArrowRight className="w-4 h-4" />
      </a>
    </section>
  );
};

export default function CongratsFunnelPage() {
  const { lang, variant } = useParams();
  useEffect(() => { document.documentElement.lang = lang === "cz" ? "cs" : "sk"; }, [lang]);
  const c = findCongrats(lang, variant);
  const [res, setRes] = useState(null);
  if (!c) return <Navigate to="/gratulujeme/sk/web" replace />;
  const ui = CONGRATS_UI[c.lang];
  return (
    <div className="min-h-screen bg-[#0a1024] text-white" data-testid={`congrats-page-${c.lang}-${c.variant}`}>
      <Helmet htmlAttributes={{ lang: c.lang === "cz" ? "cs" : "sk" }}>
        <title>{`${ui.title} ${c.question} | TopRankPulse`}</title>
        <meta name="description" content={`${c.lead} ${c.promise}`} />
        <meta property="og:title" content={`${ui.title} ${c.adLine}`} />
        <meta property="og:image" content={c.images.google} />
      </Helmet>
      <style>{CSS}</style>
      <PersonaHeader ctaLabel={c.lang === "cz" ? "Balíčky a ceny" : "Balíky a ceny"} />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 right-[-10%] w-[640px] h-[640px] rounded-full bg-[#3b82f6]/15 blur-[120px]" />
          {SPARKS.map(([l, t, d]) => <span key={`${l}-${t}`} className="g-spark" style={{ left: `${l}%`, top: `${t}%`, animationDelay: `${d}s` }} />)}
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-24 pb-14">
          <p className="g-up inline-block px-3 py-1 mb-6 rounded-full border border-white/15 text-xs font-bold uppercase tracking-wider text-white/70" data-testid="congrats-badge">{c.badge}</p>
          <h1 className="g-up text-5xl sm:text-6xl lg:text-7xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="congrats-title">
            <span className="relative inline-block">{ui.title}<span className="absolute left-0 -bottom-2 h-2 w-full rounded bg-[#2563eb]" /></span>
          </h1>
          <p className="g-up mt-9 text-lg sm:text-2xl text-white/90 max-w-3xl leading-snug" style={{ animationDelay: ".1s" }}>{c.lead}</p>
          <p className="g-up mt-4 text-lg sm:text-2xl font-bold text-white max-w-3xl leading-snug" style={{ animationDelay: ".2s" }} data-testid="congrats-question">{c.question}</p>
          <p className="g-up mt-4 text-base sm:text-lg text-amber-200" style={{ animationDelay: ".3s" }}>{c.promise}</p>
          {res ? <QuickResult res={res} ui={ui} c={c} onReset={() => setRes(null)} /> : <CheckForm c={c} ui={ui} onResult={setRes} />}
        </div>
      </section>
      <CongratsOffer ui={ui} lang={c.lang === "cz" ? "cs" : "sk"} />
    </div>
  );
}
